import { useDebounceFn } from "@vueuse/core";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import type { ComputedRef, Ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useQuickSearchProductsQuery } from "../api/quickSearch.api";
import type { QuickSearchItem } from "../api/quickSearch.gql";

import { RouteNames } from "@/app/router/types/names";
import { PERMISSIONS, usePermissions } from "@/shared/permissions";

const SEARCH_LIMIT = 20;
const MIN_CHARS = 1;

interface UseQuickSearchReturn {
  query: Ref<string>
  isOpen: Ref<boolean>
  isLoading: ComputedRef<boolean>
  canSearch: ComputedRef<boolean>
  results: ComputedRef<QuickSearchItem[]>
  total: ComputedRef<number>
  activeIndex: Ref<number | null>
  activeSearchQuery: ComputedRef<string | null>
  close: () => void
  clear: () => void
  clearSearch: () => void
  searchAll: () => void
  handleKeydown: (e: KeyboardEvent) => void
  selectItem: (item: QuickSearchItem) => void
  requestFocus: Ref<number>
}

export function useQuickSearch(): UseQuickSearchReturn {
  const router = useRouter();
  const route = useRoute();
  const query = ref("");
  const isOpen = ref(false);
  const activeIndex = ref<number | null>(null);
  const requestFocus = ref(0);

  const { can } = usePermissions();
  const canSearch = computed(() => can(PERMISSIONS.CATALOG_READ));

  const { load, result, loading, onError } = useQuickSearchProductsQuery();

  const results = computed<QuickSearchItem[]>(
    () => result.value?.quickSearchProducts.items ?? [],
  );
  const total = computed(() => result.value?.quickSearchProducts.total ?? 0);
  const isLoading = computed(() => loading.value);
  const activeSearchQuery = computed<string | null>(
    () => (route.query.search as string) || null,
  );

  onError((err) => {
    console.error("[useQuickSearch]", err.message);
  });

  const doSearch = useDebounceFn((q: string) => {
    load(undefined, { input: { query: q, limit: SEARCH_LIMIT } });
  }, 200);

  watch(query, (val) => {
    activeIndex.value = null;
    const trimmed = val.trim();
    if (trimmed.length >= MIN_CHARS) {
      isOpen.value = true;
      doSearch(trimmed);
    }
    else {
      isOpen.value = false;
    }
  });

  function close() {
    isOpen.value = false;
    activeIndex.value = null;
  }

  function clear() {
    query.value = "";
    close();
  }

  function searchAll() {
    const trimmed = query.value.trim();
    if (!trimmed) return;
    router.push({ name: RouteNames.PRODUCTS, query: { search: trimmed } });
    query.value = "";
    close();
  }

  function clearSearch() {
    const newQuery = { ...route.query };
    delete newQuery.search;
    router.replace({ query: newQuery });
  }

  function selectItem(item: QuickSearchItem) {
    router.push({ name: RouteNames.PRODUCT_DETAIL, params: { id: item.id } });
    clear();
  }

  function handleKeydown(e: KeyboardEvent) {
    if (!isOpen.value) return;

    const lastProductIndex = results.value.length - 1;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (activeIndex.value === null) {
          activeIndex.value = -1;
        }
        else if (activeIndex.value < lastProductIndex) {
          activeIndex.value++;
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (activeIndex.value === null) {
          break;
        }
        else if (activeIndex.value === -1) {
          activeIndex.value = null;
        }
        else if (activeIndex.value === 0) {
          activeIndex.value = -1;
        }
        else {
          activeIndex.value--;
        }
        break;
      case "Enter":
        e.preventDefault();
        if (activeIndex.value === null || activeIndex.value === -1) {
          searchAll();
        }
        else {
          const item = results.value[activeIndex.value];
          if (item) selectItem(item);
        }
        break;
      case "Escape":
        close();
        break;
    }
  }

  function handleGlobalKeydown(e: KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      e.preventDefault();
      requestFocus.value++;
    }
  }

  onMounted(() => {
    document.addEventListener("keydown", handleGlobalKeydown);
  });
  onUnmounted(() => {
    document.removeEventListener("keydown", handleGlobalKeydown);
  });

  return {
    query,
    isOpen,
    isLoading,
    canSearch,
    results,
    total,
    activeIndex,
    activeSearchQuery,
    close,
    clear,
    clearSearch,
    searchAll,
    handleKeydown,
    selectItem,
    requestFocus,
  };
}
